import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { components } from 'react-select'
import { updateSearchQuery, updateMessageFilter } from '../../actions/chat'

import Search from '../Search/Search'
import DialogsSection from '../DialogsSection'
import Select from 'uiKit/Select/Select'
import {
  getAllSupportUsers
} from '../../api/users'
import {
  saveAllSupportUsers,
  saveMoreAllSupportUsers,
} from '../../actions/users'

import { useDebounce } from 'hooks/useDebounce'
import { isElementBottom } from 'helpers/isElementBottom'
import { getUrlEnd } from 'helpers/getUrlEnd'
import { SUPPORT_USERS_SIZE } from 'constants/userSize'
import { SupportUserType } from 'models/SupportUserType'
import { getUserRole, ROLE } from 'security'
import { HOME_PATH } from 'configs'

import * as S from './OpenRequests.styles'

interface Props {
  match: any
  history: any
  assignedToMeUsers: SupportUserType[]
  newRequestsUsers: SupportUserType[]
  expiredRequestsUsers: SupportUserType[]
  assignedRequestsUsers: SupportUserType[]
  allUsers: SupportUserType[]
  adminUserId: number
}

const MESSAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'USEFUL', label: 'Useful' },
  { value: 'NEEDS_REVIEW', label: 'Less Useful' },
]

const RadioOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <S.RadioCircle isSelected={props.isSelected} />
        {props.label}
      </div>
    </components.Option>
  )
}

const filterSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    borderRadius: 8,
    border: '1px solid rgba(244, 244, 246, 1)',
    padding: '0 6px',
    minHeight: 32,
    height: 32,
    backgroundColor: 'rgba(255, 255, 255, 1)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--color-hawkes-blue)' : 'none',
    '&:hover': {
      border: '1px solid rgba(244, 244, 246, 1)',
    },
    cursor: 'pointer',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontFamily: "'Lato', sans-serif",
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    color: 'rgba(136, 136, 136, 1)',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: 'white',
    color: 'rgba(30, 30, 30, 1)',
    fontFamily: "'Lato', sans-serif",
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
    padding: '10px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--color-hawkes-blue)',
    },
    '&:active': {
      backgroundColor: 'var(--color-hawkes-blue)',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    color: 'var(--color-text-form-label)',
    padding: '0 4px',
    transition: 'all .2s ease',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
    '&:hover': {
      color: 'var(--color-text-form-label)',
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: 6,
    border: '1px solid rgba(224, 224, 224, 1)',
    padding: 4,
    backgroundColor: 'var(--Neutral-White, rgba(255, 255, 255, 1))',
    boxShadow: '0px 0px 1px 0px rgba(0, 0, 0, 0.3), 0px 0px 4px 0px rgba(0, 0, 0, 0.15)', // Approximate shadow based on var usage
    zIndex: 10000,
  }),
}

export const OpenRequests: React.FC<Props> = ({
  match,
  history,
  assignedToMeUsers,
  newRequestsUsers,
  expiredRequestsUsers,
  assignedRequestsUsers,
  allUsers,
  adminUserId,
}) => {
  const [search, setSearch] = useState('')
  const [hasNext, setHasNext] = useState(true)
  const [loadingChatbot, setLoadingChatbot] = useState(false)
  const [messageFilter, setMessageFilter] = useState(MESSAGE_FILTER_OPTIONS[0])

  const dispatch = useDispatch()
  const activeUser = useSelector((state: any) => state.activeUser)
  const activeBot = useSelector((state: any) => state.activeBot)
  const debouncedSearch = useDebounce(search, 500)
  const isAdmin = [ROLE.ADMIN, ROLE.CARECO_ADMIN].includes(getUserRole(Number(match?.params?.botId)))

  useEffect(() => {
    dispatch(updateSearchQuery(debouncedSearch))
  }, [debouncedSearch])

  useEffect(() => {
    setHasNext(true)
    loadAllSupportUsers(saveAllSupportUsers, 0)
  }, [debouncedSearch, messageFilter])

  const handleScroll = event => {
    if (isElementBottom(event.target) && !loadingChatbot && hasNext) {
      loadAllSupportUsers(saveMoreAllSupportUsers, allUsers.length)
    }
  }

  const getFeedbackParam = () => {
    return messageFilter.value === 'all' ? '' : messageFilter.value
  }

  const loadAllSupportUsers = (callback, offset) => {
    setLoadingChatbot(true)
    getAllSupportUsers(match.params.botId, debouncedSearch, offset, getFeedbackParam())
      .then(res => {
        callback(res)
        if (res.length < SUPPORT_USERS_SIZE) setHasNext(false)
      })
      .finally(() => setLoadingChatbot(false))
  }

  const handleFilterChange = (option: any) => {
    setMessageFilter(option)
    dispatch(updateMessageFilter(option.value))
    if (activeUser?.chatId) {
      history.push(`${HOME_PATH}/bot/${match.params.botId}/support`)
    }
  }

  return (
    <>
      <S.SearchContainer>
        <Search search={search} handleSearch={setSearch} />
      </S.SearchContainer>
      {isAdmin && activeBot?.isAiFeedbackEnabled && (
        <S.FilterRow>
          <S.FilterLabel>Message filter</S.FilterLabel>
          <S.FilterSelectWrapper>
            <Select
              value={messageFilter}
              options={MESSAGE_FILTER_OPTIONS}
              onChange={handleFilterChange}
              isSearchable={false}
              components={{ Option: RadioOption }}
              styles={filterSelectStyles}
            />
          </S.FilterSelectWrapper>
        </S.FilterRow>
      )}
      <S.SectionWrap onScroll={handleScroll}>

        <DialogsSection title="" search={debouncedSearch} users={allUsers} loading={loadingChatbot} messageFilter={messageFilter.value} />
      </S.SectionWrap>
    </>
  )
}
